import { useQuery } from '@tanstack/react-query';
import { AutoComplete, Input } from 'antd';
import { useState } from 'react';
import { ARRANGEMENT_LIMITS, PLACE_MASTER_NAME_MAX, type PlaceSuggestionsResponse } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { useDebounced } from '@/hooks/useDebounced';

/**
 * A duty location: pick one from the gazetteer, or type one that is not in it.
 *
 * ⚠️ AN `AutoComplete`, NOT `pages/Places/components/PlacePicker.tsx`'s `Select`.
 *
 * `PlacePicker` was written for the merge dialog, where the answer MUST be an existing
 * place, and its own header nominates the arrangement form as the second consumer that
 * would promote it to `components/`. That promotion is the wrong move here: FR-5.7 and the
 * brief both require FREE TEXT ALWAYS, because "Naka point, Gate 4" is a real duty
 * location no gazetteer knows, and a `Select` cannot return a value that is not an option.
 * A form that refuses the operator's own words is a form they stop using.
 *
 * So this returns `{ placeId, name }` where `placeId` is null for free text. Nothing here
 * WRITES to the place master — a `NODAL_READ` officer may raise an arrangement and holds
 * no `place.write`, and creating a gazetteer entry as a side effect of typing would be a
 * privilege escalation dressed as a convenience.
 */

export type PlaceValue = { placeId: string | null; name: string };

export type PlaceAutocompleteProps = {
  value?: PlaceValue | undefined;
  onChange?: (value: PlaceValue) => void;
  disabled?: boolean;
  'data-testid'?: string;
};

export const PlaceAutocomplete = ({ value, onChange, disabled, 'data-testid': testId }: PlaceAutocompleteProps) => {
  const [search, setSearch] = useState('');

  /*
   * ⚠️ [C3c §14b] DEBOUNCED, AND UNTIL THIS PASS IT WAS NOT.
   *
   * `onSearch` wrote straight into state, so a request went out per KEYSTROKE past three
   * characters, from this control and from `PlaceDialog`'s near-duplicate probe, with no
   * per-caller meter behind either. Geocoding is inert today ([C3b §7]: nothing writes
   * `external_place_id`, so the join can never match) — which is precisely what makes now the
   * cheap moment to fix it, before a vendor is turned on and every keystroke is billed.
   *
   * ⚠️ THE THREE-CHARACTER THRESHOLD AND THE `staleTime` BOTH STAY. They are different bounds:
   * one is how OFTEN we ask, one is how LITTLE is worth asking about, and one is how long an
   * answer keeps.
   */
  const trimmed = useDebounced(search).trim();
  /*
   * ─── ⚠️ [C2b §2] `GET /places/suggestions`, NOT `GET /places` ───────────────────────────
   *
   * This control queried the gazetteer LIST, which is a plain paginated `find()` and knows
   * nothing about the geocoding seam. So the provider that has existed since [P1b] was
   * unreachable from the one screen that picks a place: implementing Google would have changed
   * nothing an operator could see, which is the shape of defect this seam was supposed to make
   * impossible.
   *
   * ⚠️ `suggestPlaces` IS THE MERGE POINT AND IS ALREADY WRITTEN. It scores the local matches,
   * asks the provider, and surfaces a place we already hold that the local pass missed —
   * matched on the provider's own stable id. With the gate closed it answers the local set
   * alone, so this change is a NO-OP on every deployment that has not set a key.
   *
   * ⚠️ AND IT STILL RETURNS EXISTING RECORDS ONLY. An external result that has never been
   * imported carries no place id and cannot be offered — importing it is a WRITE, and a
   * `NODAL_READ` officer holds no `place.write`. That rule is the service's and is untouched:
   * free text remains the answer for a place the gazetteer does not have.
   */
  const params = { name: trimmed };
  const places = useQuery({
    queryKey: queryKeys.places.suggestions(trimmed),
    queryFn: ({ signal }) => api.get<PlaceSuggestionsResponse>('/places/suggestions', { query: params, signal }),
    // Below three characters every place is a "match", which is noise rather than help —
    // the same threshold `PlaceDialog` uses for its near-duplicate probe.
    // [PLACE-500] And above the gazetteer's own limit there is nothing to match: a duty place may be 500
    // characters, the probe refuses a term over 200 with a 422, and no master row is longer.
    enabled: trimmed.length >= 3 && trimmed.length <= PLACE_MASTER_NAME_MAX,
    staleTime: 30_000,
  });

  const options = (places.data?.suggestions ?? []).map(({ place }) => ({
    value: place.name,
    label: place.formattedAddress ? `${place.name} — ${place.formattedAddress}` : place.name,
    placeId: place.id,
  }));

  return (
    <AutoComplete
      className="w-full"
      disabled={disabled}
      value={value?.name ?? ''}
      options={options}
      // The server already prefix-matched over the alias-aware term array; filtering again
      // here would hide a match whose text never appears in the label.
      filterOption={false}
      onSearch={setSearch}
      onSelect={(name: string, option: { placeId?: string }) => {
        onChange?.({ placeId: option.placeId ?? null, name });
        /* ⚠️ [C3c §14b] The term outlived the selection, so the query stayed live under a place
           the operator had already chosen — a request per mounted control, for ever. */
        setSearch('');
      }}
      /*
       * ⚠️ TYPING CLEARS `placeId`. The moment the text stops being the chosen place's
       * name, the id is a claim about a different place — and a stale id is what would
       * quietly increment the usage count of somewhere the duty is not.
       */
      onChange={(name: string) => onChange?.({ placeId: null, name })}
      data-testid={testId}
    >
      {/* [PLACE-500] The server's own bound, so a 501st character cannot reach a save it would refuse. */}
      <Input
        placeholder="Search, or type a place that is not on the list"
        aria-label="Place"
        maxLength={ARRANGEMENT_LIMITS.PLACE_NAME}
      />
    </AutoComplete>
  );
};
